import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { COLORS, TYPOGRAPHY, CHAT_TOKENS } from '../../../styles/UISystem';
import { ContactShareMessage } from './ContactShareMessage';
import { linkifyContactSegments } from '../../../utils/linkifyText';
import { openContactLink } from '../../../utils/contactKeys';
import { MapPin } from '@phosphor-icons/react/dist/ssr/MapPin';
import { Check } from '@phosphor-icons/react/dist/ssr/Check';
import { PencilSimple } from '@phosphor-icons/react/dist/ssr/PencilSimple';

// Classifies a system-pill line as a date line. [,\s] — legacy baked texts
// (migration-skipped rows, stale caches) start "Thur, Apr 30…" (comma); the
// live pill format is "Thu Jun 18…" (space).
const SYSTEM_DATE_LINE_PATTERN = /^(Sun|Mon|Tue|Wed|Thu|Thur|Fri|Sat)[,\s]/i;

/**
 * ChatMessage Component
 *
 * Displays a chat message bubble that can be either sent or received
 * - Bubble expands to fit text, never going more than 70% of screen
 * - Text justifies left in text bubble
 * - Received: BodyRegular text, Background DISABLED_GRAY color, bubble justifies to left of screen
 * - Sent: BodyRegular text (White color), Background MEDIUM_BLUE color, bubble justifies to the right of screen
 * - System: Centered text for system messages like date/time/location (static pill, no editing)
 * - Contact: System message with contact type icon
 */
export const ChatMessage = ({
  message,
  type = 'received',
  onClick,
  showMapPin = true,
  onConfirmVenue = null,
}) => {
  // Handle different message formats according to data architecture
  let formattedMessage = message;

  // Handle object messages
  if (typeof message === 'object' && message !== null) {
    // Special handling for system messages
    if (type === 'system') {
      console.log('[CHAT] Processing system message object');

      // For system messages, try to extract location and date information
      if (message.content && typeof message.content === 'string') {
        formattedMessage = message.content;
      } else if (message.chatRoomId && message.connectionMetadata) {
        // This is likely a system message with connection metadata
        // Format it nicely instead of showing raw JSON
        try {
          const metadata = message.connectionMetadata;
          formattedMessage = `Connected in ${metadata.neighborhood || 'unknown location'}`;
          if (metadata.timestamp) {
            const date = new Date(metadata.timestamp);
            formattedMessage += ` // ${date.toLocaleDateString()} (${date.toLocaleTimeString()})`;
          }
        } catch (e) {
          console.error('[CHAT] Error formatting system message:', e);
          formattedMessage = 'Connection established';
        }
      } else {
        // Generic fallback for system messages
        formattedMessage = 'System notification';
      }
    }
    // Handle contact/sent messages
    else if (message.content) {
      formattedMessage = message.content;
    } else if (message.anonData && message.anonData.name) {
      // Format contact message with name and contact type
      const contactType =
        message.contactType || (message.anonData && message.anonData.contactType) || 'contact';
      formattedMessage = `${message.anonData.name} shared ${contactType}`;
    } else if (typeof message === 'object') {
      // Try to extract any useful information from the object
      const keys = Object.keys(message);
      if (keys.includes('chatRoomId')) {
        // This is likely a raw message object, extract a meaningful string
        formattedMessage = 'Message received';
      } else {
        formattedMessage = 'Contact shared';
      }
    } else {
      formattedMessage = 'Contact shared';
    }
  }
  // Handle JSON string messages (legacy format)
  else if (typeof message === 'string' && message.startsWith('{') && message.endsWith('}')) {
    try {
      const parsedMessage = JSON.parse(message);
      // Extract content based on parsed structure
      if (parsedMessage && parsedMessage.content) {
        formattedMessage = parsedMessage.content;
      } else if (parsedMessage && parsedMessage.anonData && parsedMessage.anonData.name) {
        // Format contact message with name and contact type
        const contactType =
          parsedMessage.contactType ||
          (parsedMessage.anonData && parsedMessage.anonData.contactType) ||
          'contact';
        formattedMessage = `${parsedMessage.anonData.name} shared ${contactType}`;
      } else if (parsedMessage && typeof parsedMessage === 'object') {
        // Try to extract any useful information from the object
        if (parsedMessage.nameValue) {
          const contactType = parsedMessage.contactType || 'contact';
          formattedMessage = `${parsedMessage.nameValue} shared ${contactType}`;
        } else if (Object.keys(parsedMessage).includes('chatRoomId')) {
          // This is likely a raw message object
          formattedMessage = 'Message received';
        } else {
          // Last resort - use a generic message
          formattedMessage = 'Contact shared';
          console.log('[CHAT] Unrecognized message format:', parsedMessage);
        }
      }
    } catch (e) {
      // Keep original message if parsing fails
      console.log('[CHAT] Failed to parse message JSON:', e.message);
    }
  }

  // Determine if this is a sent message, system message, or contact message
  const isSent = type === 'sent';
  const isSystem = type === 'system';

  // Linkify once per message text, not per list render — the composer's state
  // lives in ConnectionPageShared, so the whole list re-renders per keystroke
  // and two regex scans per bubble add up in long rooms. null = branch inactive.
  const linkSegments = useMemo(
    () =>
      !isSent && typeof formattedMessage === 'string'
        ? linkifyContactSegments(formattedMessage)
        : null,
    [isSent, formattedMessage]
  );

  // Contact messages no longer need separate handling — rendered as regular messages without icons

  // For system messages (like date/time/location), display FrostedPill style
  if (isSystem) {
    // Split message: venue (line 1), address (line 2), date/time (line 3)
    const lines = formattedMessage.split('\n').filter((line) => line.trim() !== '');

    // Determine which lines are venue, address, and date/time
    // If 3 lines: venue, address, datetime
    // If 2 lines: could be (venue + datetime) or (address + datetime)
    // If 1 line: just datetime or location
    let venueLine = '';
    let dateTimeLine = '';

    if (lines.length >= 3) {
      venueLine = lines[0];
      // Skip address line (lines[1]) for compact display
      dateTimeLine = lines[2];
    } else if (lines.length === 2) {
      // Check if first line looks like a date (contains day abbreviation)
      if (SYSTEM_DATE_LINE_PATTERN.test(lines[0])) {
        // First line is date, second is location
        dateTimeLine = lines[0];
        venueLine = lines[1];
      } else {
        // First line is location, second is date
        venueLine = lines[0];
        dateTimeLine = lines[1];
      }
    } else if (lines.length === 1) {
      // Single line - check if it's a date or location
      if (SYSTEM_DATE_LINE_PATTERN.test(lines[0])) {
        dateTimeLine = lines[0];
      } else {
        venueLine = lines[0];
      }
    }

    // One condition for both halves of the venue-confirm icon pair
    // (pencil inside the pill + floating check) — they can't drift (/simplify)
    const showVenuePair = Boolean(onConfirmVenue && venueLine);

    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          marginBottom: '18px',
          marginTop: '-8px',
          width: '100%',
        }}
      >
        {/* position:relative anchor so the confirm check floats beside the pill
            without shifting it off center (venue confirm affordance, contract #7) */}
        <div style={{ position: 'relative', display: 'inline-flex' }}>
          <div
            onClick={onClick}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '10px',
              backgroundColor: onClick ? 'rgba(255, 255, 255, 0.95)' : 'transparent',
              borderRadius: '16px',
              padding: '8px 14px',
              cursor: onClick ? 'pointer' : 'default',
              boxShadow: onClick ? CHAT_TOKENS.SYSTEM_SHADOW : 'none',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              {/* Location line with optional MapPin icon */}
              {venueLine && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                >
                  {showMapPin && <MapPin size={18} color="#8E8E93" weight="regular" />}
                  <span
                    style={{
                      fontSize: '16px',
                      fontWeight: '500',
                      color: '#1C1C1E',
                    }}
                  >
                    {venueLine}
                  </span>
                </div>
              )}
              {/* Date/time line */}
              {dateTimeLine && (
                <span
                  style={{
                    fontSize: '14px',
                    fontWeight: '400',
                    color: '#8E8E93',
                    marginTop: venueLine ? '2px' : '0',
                  }}
                >
                  {dateTimeLine}
                </span>
              )}
            </div>

            {/* Quiet pencil — the edit half of the icon pair (exchange-polish
                item 6 + device iteration: same size and vertical midline as the
                check). Rides the pill's existing tap (LocationOverlay). */}
            {showVenuePair && (
              <PencilSimple size={20} color="#C7C7CC" weight="regular" style={{ flexShrink: 0 }} />
            )}
          </div>

          {/* Confirm check — bare glyph, no circle (device iteration 2026-07-21:
              a circle gave the accept CTA weight; the accuracy gate stays quiet).
              Padded to a ~44px hit area. Check = confirm-and-share: the suggested
              venue becomes the confirmed venue and the peer's banner upgrades
              (the display gate reads venueConfirmed). */}
          {showVenuePair && (
            <button
              aria-label="Confirm venue"
              onClick={onConfirmVenue}
              style={{
                position: 'absolute',
                left: '100%',
                top: '50%',
                transform: 'translateY(-50%)',
                marginLeft: '2px',
                padding: '12px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              <Check size={20} color="#8E8E93" weight="regular" />
            </button>
          )}
        </div>
      </div>
    );
  }

  // Check if this is a contact sharing message
  let isContactShare = false;

  // Check for contact share message by looking for 'shared' text
  // or checking if there's a contactValue field which is a strong indicator
  if (typeof message === 'object' && message !== null && message.contactValue) {
    isContactShare = true;
  }
  // Fall back to checking the formatted message
  else if (typeof formattedMessage === 'string' && formattedMessage.includes('shared')) {
    isContactShare = true;
  }

  // Extract name from contact share message
  let contactName = '';

  if (isContactShare) {
    // First check for name in anonData (most reliable source)
    if (
      typeof message === 'object' &&
      message !== null &&
      message.anonData &&
      message.anonData.name
    ) {
      contactName = message.anonData.name;
      console.log('[CHAT_MESSAGE] Using name from anonData:', contactName);
    }
    // Check if we have contactValue but need to extract name from the main content
    else if (
      typeof message === 'object' &&
      message !== null &&
      message.contactValue &&
      typeof message.content === 'string'
    ) {
      const contentMatch = message.content.match(/^([\w\s]+) shared/i);
      if (contentMatch && contentMatch[1]) {
        contactName = contentMatch[1].trim();
        console.log('[CHAT_MESSAGE] Using name from content with contactValue:', contactName);
      }
    }
    // Then try to extract from formatted message
    else if (typeof formattedMessage === 'string') {
      // Try to extract name from format "Name shared contact"
      const shareMatch = formattedMessage.match(/^([\w\s]+) shared/i);
      if (shareMatch && shareMatch[1]) {
        contactName = shareMatch[1].trim();
        console.log('[CHAT_MESSAGE] Using name from formattedMessage match:', contactName);
      } else {
        // Last resort: try to get first part of the message as name
        contactName = formattedMessage.split(' ')[0];
        console.log('[CHAT_MESSAGE] Using first word as name:', contactName);
      }
    }
  }

  // Regular chat messages
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: isSent ? 'flex-end' : 'flex-start',
        marginBottom: '14px',
        width: '100%',
      }}
    >
      <div
        style={{
          padding: isContactShare ? '10px 12px' : '10px 16px', // Slightly different padding for contact share
          borderRadius: isSent
            ? CHAT_TOKENS.BUBBLE_RADIUS_SENT
            : CHAT_TOKENS.BUBBLE_RADIUS_RECEIVED,
          backgroundColor: isSent ? COLORS.CHAT_SENT : COLORS.RECEIVED_GRAY,
          maxWidth: '85%',
          wordBreak: 'break-word',
          ...TYPOGRAPHY.BODY_REGULAR,
          color: isSent ? COLORS.WHITE : COLORS.CHAT_TEXT_DARK,
          textAlign: 'left',
        }}
      >
        {isContactShare && contactName ? (
          <ContactShareMessage
            name={contactName}
            isSent={isSent}
            // onClick removed - deprecated feature (contact tap to profile)
          />
        ) : linkSegments ? (
          // Received bubbles linkify contact-shaped content (device pass 10:
          // a number or email shared in ANY message is tappable for the
          // receiver — post-classification chat included)
          linkSegments.map((seg, i) =>
            seg.href ? (
              <span
                key={i}
                // stopPropagation: the segment's channel action must not also
                // fire a wrapping bubble tap (Anon-side door to detail)
                onClick={(e) => {
                  e.stopPropagation();
                  openContactLink(seg.href);
                }}
                style={{
                  textDecoration: 'underline',
                  textUnderlineOffset: '3px',
                  cursor: 'pointer',
                }}
              >
                {seg.text}
              </span>
            ) : (
              <span key={i}>{seg.text}</span>
            )
          )
        ) : (
          formattedMessage
        )}
      </div>
    </div>
  );
};

ChatMessage.propTypes = {
  /** The message text to display */
  message: PropTypes.string.isRequired,
  /** The type of message: 'sent', 'received', 'system', or 'contact' */
  type: PropTypes.oneOf(['sent', 'received', 'system', 'contact']),
  /** The type of contact being shared (phone, email, instagram) */
  contactType: PropTypes.string,
  /** Click handler (used for system messages to navigate to detail page) */
  onClick: PropTypes.func,
  /** Whether to show MapPin icon in system messages (default true) */
  showMapPin: PropTypes.bool,
  /** Venue confirm affordance (system pill only): a quiet pencil beside the venue
      line (edit — rides the pill tap) and a floating check circle beside the pill
      (accept). Check = confirm-and-share. */
  onConfirmVenue: PropTypes.func,
};

/**
 * ChatMessage Example Component for UI Showcase
 */
export const ChatMessageExample = () => {
  return (
    <div style={{ width: '100%', padding: '16px' }}>
      <h3 style={{ marginBottom: '16px', textAlign: 'center' }}>ChatMessage</h3>

      <div style={{ marginBottom: '24px' }}>
        <h4 style={{ marginBottom: '8px' }}>Received</h4>
        <ChatMessage message="Hi there, what's up" type="received" />
      </div>

      <div>
        <h4 style={{ marginBottom: '8px' }}>Sent</h4>
        <ChatMessage message="Hi nice to meet, how's everything going with you?" type="sent" />
      </div>
    </div>
  );
};
